import { NavSection } from 'shared/components/configPanel/SelectionPanel';
import { slugOf } from 'shared/components/configPanel/actionSetup';
import { isActionVisible } from 'shared/components/forms/SchemaFormTemplates/checkSchema';
import {
  ADVANCED_CAT,
  buildActionNav,
} from 'shared/components/configPanel/buildActionNav';
import { payloadParsingNotRequired } from 'shared/components/configPanel/tempExemptions';
import { getRouteKind } from 'shared/utils/routeKind';
import { AccountBundle } from '../flowMapTypes';
import { FlowArrow } from '../useFlowGraph';
import { dormantFlowTypes, exemptSlugsFor } from '../needsSetup';
import { AccountBaseUrl } from './generalFields';

type Translate = (key: any, options?: any) => string;

// A destination that sends data back is a data provider too — its developers
// need its own API key, not just the primary provider's.
export const sendsDataBack = (accountId: string, arrows: FlowArrow[]) =>
  arrows.some(
    (a) =>
      a.destinationId === accountId &&
      a.direction === 'in' &&
      a.state === 'enabled',
  );

// The webhook configuration an account's panel edits: only the Route's own
// provider has one, and only when its type carries a webhook. A provider that
// never saved one gets an empty configuration to start from.
export const webhookConfigFor = (bundle: AccountBundle, routeProvider: any) =>
  bundle.isProvider &&
  (routeProvider?.webhook_configuration || routeProvider?.type?.webhook)
    ? (routeProvider.webhook_configuration ?? {
        id: '',
        integration: routeProvider.id,
        webhook: routeProvider.type?.webhook ?? {
          id: '',
          name: '',
          value: '',
        },
        data: null,
      })
    : undefined;

// Everything the account panel's nav shows, and which section is open.
export function accountNav({
  bundle,
  urlState,
  arrows,
  providerId,
  routeProvider,
  bundles,
  destinations,
  section,
  t,
}: {
  bundle: AccountBundle;
  urlState: AccountBaseUrl;
  arrows: FlowArrow[];
  providerId: string;
  routeProvider: any;
  bundles: Record<string, AccountBundle>;
  destinations: any[];
  // The section the selection asks for, if any.
  section?: string;
  t: Translate;
}) {
  const actions = bundle.actions.filter(isActionVisible);
  // A flow direction no enabled arrow is carrying is dormant: its sections
  // show as optional FYIs instead of carrying "!" badges — the same rule the
  // node badge and the Quick Start count already apply in computeNeedsSetup.
  const dormant = dormantFlowTypes(bundle.id, arrows);
  // GUNDI-5680 temporary hack: sections that never badge on this account
  // given who is at the other end of the Route (see tempExemptions.ts).
  const exemptSlugs = exemptSlugsFor(bundle, {
    providerId,
    routeProvider,
    bundles,
    destinations,
  });
  // Every action the account owns is configured here, grouped by what it
  // does: credentials, then the directions data can move (pull = this
  // system sends, push = it receives), with anything else under Advanced
  // Config at the bottom. The account's name heads the list — a Connection
  // can be renamed from either side of the map.
  // The action grouping is the shared one, so this panel and the Connection
  // page can't drift apart (a hand-rolled copy here put an auth action typed
  // `pull` in both Credentials and Send data).
  const nav: NavSection[] = buildActionNav({
    target: bundle,
    actions,
    t,
    detailsSlug: 'general',
    // One Details pane carries the account's name, workspace and endpoint.
    // Whether the endpoint shows, and whether it may stay empty, is the
    // shared base-URL policy's call.
    detailsNeedsSetup: urlState.show && !urlState.optional && !bundle.base_url,
    dormantTypes: dormant,
    exemptSlugs,
  });
  const sendsBack = !bundle.isProvider && sendsDataBack(bundle.id, arrows);
  const providerType = bundle.isProvider ? routeProvider?.type : undefined;
  // Webhook providers keep the webhook endpoint + headers; everything else
  // gets the sensors endpoint. A type that carries a webhook is a webhook
  // here even when it also has pull config (kind would say "standard").
  const routeKind: 'webhook' | 'api-push' =
    providerType &&
    (getRouteKind(providerType) === 'webhook' || providerType.webhook)
      ? 'webhook'
      : 'api-push';
  // Webhook payload parsing is provider-side config, so it lives on this
  // panel (it used to be its own tab).
  const webhookConfig = webhookConfigFor(bundle, routeProvider);
  // Insert ahead of the catch-all group so Advanced Config stays last.
  // Re-found per insert: splicing shifts the catch-all group's index, and a
  // cached one put the second insert ahead of the first (Connect before
  // Webhook) instead of both before Advanced Config.
  const insertBeforeAdvanced = (navSection: NavSection) => {
    const advancedAt = nav.findIndex((s) => s.cat === t(ADVANCED_CAT));
    if (advancedAt === -1) nav.push(navSection);
    else nav.splice(advancedAt, 0, navSection);
  };
  if (webhookConfig) {
    insertBeforeAdvanced({
      cat: t('Webhook'),
      // GUNDI-5680 temporary hack: never badged on InReach, where payload
      // parsing is not required (see tempExemptions.ts).
      items: [
        {
          slug: 'webhook',
          label: t('Payload parsing'),
          needsSetup:
            !routeProvider?.webhook_configuration?.data &&
            !payloadParsingNotRequired(routeProvider?.type?.value),
        },
      ],
    });
  }
  // Last section, and present on every account that acts as a data
  // provider: the primary provider always, and a destination once it sends
  // data back — both sides of a bidirectional Route have their own API key.
  if (providerType || sendsBack) {
    insertBeforeAdvanced({
      cat: t('Connect'),
      items: [{ slug: 'how-to-connect', label: t('Push API key') }],
    });
  }

  // Old bookmarks may still address the split sections, and a section the
  // URL names but this account doesn't offer would render an empty panel —
  // fall back to the first section either way.
  const requestedSlug =
    section === 'name' || section === 'endpoint' ? 'general' : section;
  const navSlugs = nav.flatMap((group) => group.items.map((item) => item.slug));
  const activeSlug: string | undefined =
    (requestedSlug && navSlugs.includes(requestedSlug)
      ? requestedSlug
      : undefined) || nav[0]?.items[0]?.slug;
  const activeAction = actions.find((a) => slugOf(a) === activeSlug);

  return {
    nav,
    dormant,
    exemptSlugs,
    routeKind,
    webhookConfig,
    activeSlug,
    activeAction,
  };
}
